import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme, THEME_META, YOUTH_THEMES, ADULT_THEMES } from "../context/ThemeContext";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import TrophyBadge from "./TrophyBadge";
import ThemeOnboardingModal from "./ThemeOnboardingModal";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import {
  Music2, Piano, Calendar as CalendarIcon, ListMusic, Users, Receipt, Layers,
  Award, LayoutDashboard, Palette, LogOut, ShieldCheck, Sparkles, Menu, FileMusic, Star, CreditCard, Search, Clock, AlertCircle, BookOpen, Gift, Globe,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { cn } from "../lib/utils";

function PianoLogo({ className = "" }) {
  return (
    <img src="/octave-icon.png" alt="Octave" className={`object-contain ${className}`} />
  );
}

function PremiumBackgroundPicker({ user, refresh }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error(t("Please pick an image under 3 MB.")); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        await apiClient.put("/me/premium-cosmetics", { bg_image_data_url: reader.result });
        await refresh?.();
        toast.success(t("Background set"));
      } catch (err) { toast.error(err?.response?.data?.detail || t("Upload failed")); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(f);
  };
  const clear = async () => {
    setBusy(true);
    try {
      await apiClient.put("/me/premium-cosmetics", { bg_image_data_url: "" });
      await refresh?.();
      toast.success(t("Background cleared"));
    } catch (err) { toast.error(err?.response?.data?.detail || t("Update failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4" data-testid="premium-bg-picker">
      <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "var(--app-text-muted)" }}>
        {t("Custom background")}
      </div>
      <div className="flex gap-2 items-center flex-wrap">
        <label className="btn-pill btn-outline text-xs cursor-pointer">
          {busy ? t("Saving…") : t("Upload image")}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="premium-bg-input" />
        </label>
        {user?.bg_image_data_url && (
          <button type="button" onClick={clear} className="btn-pill btn-outline text-xs" data-testid="premium-bg-clear">{t("Remove")}</button>
        )}
      </div>
    </div>
  );
}

/** Each link is a fat, unmistakable row with a colored icon tile */
function ThemeRow({ id, theme, setTheme, user, t }) {
  const meta = THEME_META[id];
  return (
    <DropdownMenuItem
      onClick={() => setTheme(id)}
      data-testid={`theme-${id}`}
    >
      <span className="w-5 h-5 rounded-full mr-2 border shrink-0" style={{ background: meta.swatch, borderColor: "rgba(0,0,0,0.15)" }} />
      <span className="flex-1">{theme === id ? "✓ " : ""}{t(meta.label)}</span>
    </DropdownMenuItem>
  );
}

const TEACHER_LINKS = [
  { to: "/teacher",               end: true, label: "Dashboard",     icon: LayoutDashboard, tint: "#0077B6", desc: "Metrics + today's lessons" },
  { to: "/teacher/curriculum",    label: "My Curriculum",             icon: BookOpen,        tint: "#06D6A0", desc: "Plan weekly focus points" },
  { to: "/teacher/students",      label: "Students",                  icon: Users,           tint: "#2D6A4F", desc: "Roster, schedules & progress" },
  { to: "/teacher/calendar",      label: "Calendar",                  icon: CalendarIcon,    tint: "#EE9B00", desc: "Month and day view" },
  { to: "/teacher/resource-hub",  label: "Resource Hub",              icon: Layers,          tint: "#9333EA", desc: "Pricing, listing, custom fields" },
  { to: "/teacher/recitals",      label: "Recitals",                  icon: Award,           tint: "#0A0A0A", desc: "Plan performances" },
  { to: "/teacher/invoices",      label: "Invoices",                  icon: Receipt,         tint: "#06D6A0", desc: "Paid & outstanding" },
  { to: "/teacher/my-music",      label: "My Music",                  icon: FileMusic,       tint: "#EF476F", desc: "Your own song library" },
  { to: "/teacher/my-scales",     label: "My Scales",                 icon: Music2,          tint: "#FFB703", desc: "Track the 12 major scales" },
  { to: "/teacher/my-piano",      label: "My Instruments",            icon: Piano,           tint: "#00B4D8", desc: "Your instruments & dream gear" },
  { to: "/referrals",             label: "Invite & earn",             icon: Gift,            tint: "#FFB703", desc: "One free month per invite" },
];

const STUDENT_LINKS = [
  { to: "/student",              end: true, label: "Dashboard",   icon: LayoutDashboard, tint: "#0077B6", desc: "Your musical home" },
  { to: "/student/calendar",     label: "Calendar",                 icon: CalendarIcon,    tint: "#EE9B00", desc: "Lessons & log practice" },
  { to: "/student/curriculum",   label: "Curriculum",               icon: BookOpen,        tint: "#06D6A0", desc: "Your teacher's plan" },
  { to: "/student/songs",        label: "Songs I'm Learning",       icon: ListMusic,       tint: "#EF476F", desc: "Pieces & progress" },
  { to: "/student/scales",       label: "My Scales",                icon: Music2,          tint: "#FFB703", desc: "Light up the keyboard" },
  { to: "/student/my-piano",     label: "My Instruments",           icon: Piano,           tint: "#00B4D8", desc: "Your instruments & dream gear" },
];

function IconTile({ Icon, tint }) {
  return (
    <span
      className="flex items-center justify-center rounded-xl w-10 h-10 shrink-0"
      style={{ background: tint, color: tint === "#FFB703" ? "#0a0a0a" : "#fff" }}
      aria-hidden
    >
      <Icon className="w-5 h-5" />
    </span>
  );
}

/** Compact pill used on wide desktop header */
function TopNavPill({ link }) {
  return (
    <NavLink
      to={link.to}
      end={link.end}
      data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
      className={({ isActive }) => cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors",
        isActive ? "text-white shadow-sm" : "hover:bg-white/70"
      )}
      style={({ isActive }) => isActive
        ? { background: link.tint, color: link.tint === "#FFB703" ? "#0a0a0a" : "#fff" }
        : { color: "var(--app-text)" }}
    >
      <link.icon className="w-4 h-4" />
      <span>{link.label}</span>
    </NavLink>
  );
}

/** Large tappable row used inside the drawer — no scrolling needed */
function DrawerRow({ link, onClick }) {
  return (
    <NavLink
      to={link.to}
      end={link.end}
      onClick={onClick}
      data-testid={`drawer-${link.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
      className={({ isActive }) => cn(
        "flex items-center gap-4 rounded-2xl px-4 py-3 border-2 transition-all",
        isActive ? "shadow-md" : "hover:-translate-y-0.5"
      )}
      style={({ isActive }) => ({
        background: isActive ? "var(--app-surface-2)" : "var(--app-surface)",
        borderColor: isActive ? link.tint : "var(--app-border)",
      })}
    >
      <IconTile Icon={link.icon} tint={link.tint} />
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-base leading-tight">{link.label}</div>
        <div className="text-xs truncate" style={{ color: "var(--app-text-muted)" }}>{link.desc}</div>
      </div>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout, subscription, refresh } = useAuth();
  const { theme, setTheme } = useTheme();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [oneTapBusy, setOneTapBusy] = useState(false);
  // First-login theme picker — opens when user has never onboarded.
  // `theme_onboarded === false` triggers the modal (undefined/null/true do not).
  const [themeOnboardOpen, setThemeOnboardOpen] = useState(false);
  useEffect(() => {
    if (user && user.theme_onboarded === false) setThemeOnboardOpen(true);
  }, [user]);

  const isStudent = user?.role === "student";
  const isDeveloper = user?.role === "developer";
  const isSolo = isStudent && user?.solo_learner;
  const links = isStudent
    ? (isSolo
        ? [
            ...STUDENT_LINKS,
            { to: "/student/find-teacher", label: "Find a Teacher", icon: Search, tint: "#9333EA", desc: "Browse teachers by city" },
            { to: "/referrals",            label: "Invite & earn",  icon: Gift,   tint: "#FFB703", desc: "One free month per invite" },
          ]
        : STUDENT_LINKS)
    : TEACHER_LINKS;
  // Translate labels + descriptions at render time so the drawer reflects the active language.
  const tLinks = links.map((l) => ({ ...l, label: t(l.label), desc: t(l.desc) }));

  const closeDrawer = () => setDrawerOpen(false);

  /** One-tap upgrade — hits Stripe Checkout directly, falling back to /subscribe if it fails. */
  const oneTapUpgrade = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (oneTapBusy) return;
    setOneTapBusy(true);
    try {
      const r = await apiClient.post("/payments/checkout", { origin_url: window.location.origin });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      nav("/subscribe");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Couldn't start checkout — opening subscribe page."));
      nav("/subscribe");
    } finally {
      setOneTapBusy(false);
      closeDrawer();
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden max-w-[100vw]" data-testid="app-layout">
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b"
        style={{ borderColor: "var(--app-border)" }}
      >
        <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-8 h-16 sm:h-20 flex items-center gap-2 sm:gap-4 min-w-0">
          {/* Drawer trigger — visible up to xl; on xl+ we also still keep it for users who prefer the big menu */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="rounded-xl h-11 sm:h-12 px-3 sm:px-4 shrink-0"
                data-testid="drawer-trigger"
                aria-label={t("Open menu")}
              >
                <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="hidden sm:inline ml-1 text-sm font-semibold">{t("Menu")}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] sm:w-[360px] p-0 flex flex-col">
              <SheetHeader className="p-5 border-b" style={{ borderColor: "var(--app-border)" }}>
                <SheetTitle className="flex items-center gap-3 font-display text-xl">
                  <PianoLogo className="w-11 h-11 shrink-0 object-contain" />
                  <div className="leading-none text-left">
                    <div>Octave</div>
                    <div className="text-[11px] font-normal mt-1 tracking-wide" style={{ color: "var(--app-text-muted)" }}>{t("Stretch your music muscles")}</div>
                  </div>
                </SheetTitle>
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--app-text-muted)" }}>
                  {isStudent ? t("Student") : isDeveloper ? t("Developer") : t("Teacher")} · {user?.name}
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="drawer-links">
                {subscription?.trial && subscription.trial_days_left <= 10 && !isDeveloper && (user?.role === "teacher" || isSolo) && (
                  <button
                    type="button"
                    onClick={oneTapUpgrade}
                    disabled={oneTapBusy}
                    data-testid="drawer-trial-countdown"
                    className="w-full text-left flex items-center gap-3 rounded-2xl px-4 py-3 border-2 transition-all animate-pulse disabled:opacity-80"
                    style={{ borderColor: "var(--app-accent)", background: "var(--app-accent)", color: "#fff" }}
                  >
                    <AlertCircle className="w-5 h-5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-base leading-tight">
                        {subscription.trial_days_left === 0
                          ? t("Your trial ends today")
                          : `${subscription.trial_days_left} ${subscription.trial_days_left === 1 ? t("day left on your free trial") : t("days left on your free trial")}`}
                      </div>
                      <div className="text-xs opacity-95">
                        {oneTapBusy ? t("Opening Stripe checkout…") : t("Tap to upgrade — $9.99/mo, one click →")}
                      </div>
                    </div>
                  </button>
                )}
                {tLinks.map((l) => <DrawerRow key={l.to} link={l} onClick={closeDrawer} />)}

                {isDeveloper && (
                  <DrawerRow
                    onClick={closeDrawer}
                    link={{
                      to: "/developer",
                      label: t("Developer Panel"),
                      icon: ShieldCheck,
                      tint: "#EF233C",
                      desc: t("Master settings & access log"),
                    }}
                  />
                )}

                {(!isStudent && !isDeveloper) || (isStudent && user?.solo_learner) ? (
                  <DrawerRow
                    onClick={closeDrawer}
                    link={{
                      to: "/subscribe",
                      label: subscription?.active ? t("Subscription") : t("Subscribe"),
                      icon: CreditCard,
                      tint: "#FFB703",
                      desc: subscription?.trial
                        ? `${subscription.trial_days_left} ${t("trial days left")}`
                        : subscription?.active ? t("Manage your plan") : t("Activate your plan"),
                    }}
                  />
                ) : null}
              </div>

              {/* Theme picker lives in the top-bar palette swatch — keep the drawer focused on
                  navigation. Premium-only background picker stays here as it's a Premium perk. */}
              {user?.premium && (
                <div className="p-4 border-t" style={{ borderColor: "var(--app-border)" }}>
                  <PremiumBackgroundPicker user={user} refresh={refresh} />
                </div>
              )}
            </SheetContent>
          </Sheet>

          <Link to={isStudent ? "/student" : "/teacher"} className="flex items-center gap-2 sm:gap-3 min-w-0 shrink" data-testid="brand-link">
            <PianoLogo className="w-9 h-9 sm:w-12 sm:h-12 shrink-0 object-contain" />
            <div className="leading-tight min-w-0">
              <div className="font-display font-bold text-base sm:text-lg truncate" style={{ color: "var(--app-text)" }}>Octave</div>
              <div className="text-[11px] tracking-wide hidden md:block truncate" style={{ color: "var(--app-text-muted)" }}>{t("Stretch your music muscles")}</div>
            </div>
          </Link>

          {/* Desktop pill nav — only appears when wide enough that EVERY pill fits, so we never need horizontal scrolling */}
          <nav className="hidden xl:flex items-center gap-1 ml-2">
            {tLinks.map((l) => <TopNavPill key={l.to} link={l} />)}
          </nav>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-3 shrink-0">
            {subscription?.trial && !isDeveloper && (user?.role === "teacher" || (user?.role === "student" && user?.solo_learner)) && (
              (() => {
                const days = subscription.trial_days_left;
                const urgent = days <= 10;
                if (urgent) {
                  return (
                    <button
                      type="button"
                      onClick={oneTapUpgrade}
                      disabled={oneTapBusy}
                      data-testid="trial-banner-link"
                      aria-label={`${days} ${t("trial days left")}`}
                      className="inline-flex items-center gap-1 btn-pill text-xs font-semibold animate-pulse disabled:opacity-80 shrink-0"
                      style={{ background: "var(--app-accent)", color: "#fff", border: "1px solid var(--app-accent)" }}
                    >
                      <Clock className="w-4 h-4 shrink-0" />
                      <span className="whitespace-nowrap">
                        {oneTapBusy
                          ? t("Opening checkout…")
                          : (days === 0 ? t("Today!") : `${days}d`)}
                      </span>
                    </button>
                  );
                }
                return (
                  <Link
                    to="/subscribe"
                    data-testid="trial-banner-link"
                    className="hidden md:inline-flex btn-pill text-xs font-semibold"
                    style={{ background: "#fff4cc", color: "#7a5a00", border: "1px solid #f3d582" }}
                    aria-label={`${days} ${t("trial days left")}`}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{days} {t("trial days left")}</span>
                  </Link>
                );
              })()
            )}
            {!subscription?.active && !isDeveloper && (user?.role === "teacher" || (user?.role === "student" && user?.solo_learner)) && (
              <button
                type="button"
                onClick={oneTapUpgrade}
                disabled={oneTapBusy}
                className="btn-pill btn-accent text-xs disabled:opacity-80"
                data-testid="subscribe-cta"
              >
                {oneTapBusy ? t("Opening…") : t("Subscribe")}
              </button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-2xl shrink-0 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-1 flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11"
                  style={{
                    background: "var(--app-surface)",
                    border: "2px solid var(--app-border)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                  data-testid="theme-menu-trigger"
                  aria-label={`Theme — current: ${THEME_META[theme]?.label || theme}`}
                  title={`Theme — ${THEME_META[theme]?.label || theme}`}
                >
                  {/* Painter palette + brush — inline SVG so it stays crisp on every theme */}
                  <svg
                    width="32" height="32" viewBox="0 0 64 64" aria-hidden="true"
                  >
                    {/* Palette body — softly off-white with a thumb hole */}
                    <path
                      d="M32 6c-14 0-25 9-25 22 0 8 5 14 12 14 3 0 4-2 4-4 0-3 3-5 6-5 5 0 7 3 7 7 0 5 5 8 10 8 7 0 11-7 11-15 0-15-11-27-25-27z"
                      fill="#FFE6C8" stroke="#C99566" strokeWidth="1.5"
                    />
                    <circle cx="44" cy="34" r="3.5" fill="#FFFDF7" />
                    {/* Paint dots — match Octave's theme palette */}
                    <circle cx="20" cy="22" r="4.2" fill="#EF476F" />
                    <circle cx="32" cy="18" r="4.2" fill="#06AEEF" />
                    <circle cx="44" cy="20" r="4.2" fill="#FFB703" />
                    <circle cx="16" cy="34" r="4.2" fill="#F08C00" />
                    <circle cx="20" cy="46" r="4.2" fill="#06D6A0" />
                    <circle cx="32" cy="50" r="4.2" fill="#1F4FE0" />
                    <circle cx="42" cy="50" r="4.2" fill="#7B2CBF" />
                    {/* Brush — wood handle, ferrule, blue-tipped bristle */}
                    <g transform="rotate(28 38 32)">
                      <rect x="14" y="29.4" width="38" height="3.2" rx="1.4" fill="#E0A675" stroke="#9B6B3A" strokeWidth="0.9"/>
                      <rect x="49" y="28.8" width="5"  height="4.4" rx="1"   fill="#B7BCC2" stroke="#5A6068" strokeWidth="0.8"/>
                      <path d="M54 29.0 q4 1 5 2 q-1 1 -5 2 z" fill="#1F2937" stroke="#0F172A" strokeWidth="0.6"/>
                      <path d="M58.6 30.2 q1.5 0 1.4 1 q0 0.7 -1.4 0.8 z" fill="#06AEEF" />
                    </g>
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[15rem] max-h-[80vh] overflow-y-auto">
                <DropdownMenuLabel>{t("Theme")}</DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest opacity-60 pt-2">
                  {t("Youth")}
                </DropdownMenuLabel>
                {YOUTH_THEMES.map((id) => <ThemeRow key={id} id={id} theme={theme} setTheme={setTheme} user={user} t={t} />)}

                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest opacity-60 pt-2">
                  {t("Adult")}
                </DropdownMenuLabel>
                {ADULT_THEMES.map((id) => <ThemeRow key={id} id={id} theme={theme} setTheme={setTheme} user={user} t={t} />)}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Language selector — Globe icon + tiny lang code badge.
                Sits next to the theme palette so both UI prefs are 1 tap apart. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="rounded-2xl shrink-0 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-1 flex items-center justify-center gap-1 px-1.5 sm:px-2"
                  style={{
                    height: 40,
                    background: "var(--app-surface)",
                    border: "2px solid var(--app-border)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                  data-testid="language-menu-trigger"
                  aria-label={`${t("Language")} — ${lang.toUpperCase()}`}
                >
                  <Globe className="w-4 h-4" style={{ color: "var(--app-primary)" }} />
                  <span className="hidden sm:inline text-xs font-bold tracking-wide">{lang.toUpperCase()}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("Language")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">
                  <span className="mr-2">🇬🇧</span>
                  {lang === "en" ? "✓ " : ""}English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLang("nl")} data-testid="lang-nl">
                  <span className="mr-2">🇳🇱</span>
                  {lang === "nl" ? "✓ " : ""}Dutch
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>


            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                  <button className="btn-pill btn-primary text-xs sm:text-sm max-w-[4.5rem] sm:max-w-[7rem] truncate px-2 sm:px-3" data-testid="user-menu-trigger">
                    {user?.name?.split(" ")[0] || t("Menu")}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isDeveloper && (
                    <DropdownMenuItem onClick={() => nav("/developer")} data-testid="menu-dev-dashboard">
                      <ShieldCheck className="w-4 h-4 mr-2" /> {t("Developer Dashboard")}
                    </DropdownMenuItem>
                  )}
                  {!isStudent && !isDeveloper && (
                    <DropdownMenuItem onClick={() => nav("/subscribe")} data-testid="menu-subscribe">
                      <CreditCard className="w-4 h-4 mr-2" /> {t("Subscription")}
                    </DropdownMenuItem>
                  )}
                  {isStudent && user?.solo_learner && (
                    <DropdownMenuItem onClick={() => nav("/subscribe")} data-testid="menu-subscribe-solo">
                      <CreditCard className="w-4 h-4 mr-2" /> {t("Subscription")}
                    </DropdownMenuItem>
                  )}
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("Sign out")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-7xl px-4 lg:px-8 py-8 pb-20">{children}</main>
      <footer
        className="fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur-md"
        style={{
          background: "color-mix(in srgb, var(--app-surface) 88%, transparent)",
          borderColor: "var(--app-border)",
        }}
      >
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-3 px-4 lg:px-8 py-2">
          <div className="text-[11px] sm:text-xs truncate" style={{ color: "var(--app-text-muted)" }}>
            {t("Octave · Built for teachers, loved by musicians.")}
          </div>
          {user && user.role !== "developer" && <TrophyBadge />}
        </div>
      </footer>
      <ThemeOnboardingModal
        open={themeOnboardOpen}
        onClose={async () => { setThemeOnboardOpen(false); try { await refresh(); } catch {} }}
      />
    </div>
  );
}
